// src/modules/app-views/serviceOrders/pages/Document/tabs/RecordTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Card, Col, Row, Form, Segmented, Collapse } from 'antd';
import {
  ToolOutlined,
  UserOutlined,
  LaptopOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import StatusTag from 'modules/app-views/serviceOrders/components/StatusTag';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ServiceOrdersRecordTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading, viewMode } = useDocumentContext() || {};
  const { form } = useSimpleFormContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';
  const isNew = !data?._id || data?._id === 'add';

  // Watch type field to show/hide conditional fields
  const currentType = Form.useWatch('type', form) || data?.type || 'budget';
  const currentStatus = Form.useWatch('status', form) || data?.status || 'draft';
  const isBudget = currentType === 'budget';
  const isOrder = currentType === 'order';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleTypeChange = useCallback(
    value => {
      form?.setFieldsValue({ type: value });

      // When switching to order, set appropriate default status
      if (value === 'order' && currentStatus === 'draft') {
        form?.setFieldsValue({ status: 'open' });
      }
      // When switching to budget, reset status to draft
      if (value === 'budget') {
        form?.setFieldsValue({ status: 'draft' });
      }
    },
    [form, currentStatus]
  );

  const handleCustomerSelect = useCallback(
    (value, option) => {
      setSelectedCustomer(option);

      // Store customer snapshot for backend
      if (option) {
        form?.setFieldsValue({
          customerId: option._id || value,
          customerSnapshot: {
            name: option.name || option.label,
            cpfCnpj: option.cpfCnpj || option.cpf || option.cnpj,
            phone: option.phone,
            email: option.email,
          },
        });
      }
    },
    [form]
  );

  const handlePriorityChange = useCallback(
    value => {
      form?.setFieldsValue({ priority: value });
    },
    [form]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="serviceOrderRecord" isFetching={loading} data={data} />

      {/* CARD 1 - IDENTIFICATION */}
      <Card title={<div className="text-muted">{translateX('identification')}</div>}>
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} sm={8} lg={6}>
            <Form.Item label={translateX('type')} name="type">
              <Segmented
                options={[
                  { label: translateX('budget'), value: 'budget' },
                  { label: translateX('order'), value: 'order' },
                ]}
                onChange={handleTypeChange}
                disabled={isLocked || (!isNew && isOrder)}
                block
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8} lg={4}>
            <Form.Item label={translateX('status')}>
              <StatusTag status={currentStatus} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8} lg={5}>
            <SimpleInput
              name="documentNumber"
              label="document_number"
              tooltip="auto_generated"
              disabled
            />
          </Col>

          <Col xs={24} sm={8} lg={5}>
            <SimpleDatePicker
              name="issueDate"
              label="issue_date"
              required
            />
          </Col>

          {isBudget && (
            <Col xs={24} sm={8} lg={4}>
              <SimpleDatePicker
                name="validUntil"
                label="valid_until"
              />
            </Col>
          )}

          {isOrder && (
            <Col xs={24} sm={8} lg={4}>
              <SimpleDatePicker
                name="orderDate"
                label="order_date"
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* CARD 2 - CUSTOMER */}
      <Card
        title={
          <div className="text-muted">
            <UserOutlined style={{ marginRight: 8 }} />
            {translateX('customer')}
          </div>
        }
      >
        <Row gutter={ROW_GUTTER}>
          <Col xs={24}>
            <Form.Item name="customerId" label={translateX('customer')}>
              <SimpleSelectAsyncEntities
                placeholder="search_customer"
                onSelect={handleCustomerSelect}
                disabled={isLocked}
                required
                selectedEntity={
                  data?.customerId
                    ? { _id: data.customerId, name: data.customerSnapshot?.name }
                    : null
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* CARD 3 - ASSET / EQUIPMENT (collapsible) */}
      <Collapse
        defaultActiveKey={data?.asset?.type ? ['asset'] : []}
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'asset',
            label: (
              <span className="text-muted">
                <LaptopOutlined style={{ marginRight: 8 }} />
                {translateX('asset')}
              </span>
            ),
            children: (
              <>
                <Row gutter={ROW_GUTTER}>
                  <Col xs={24} sm={8}>
                    <SimpleInput name={['asset', 'type']} label="asset_type" />
                  </Col>

                  <Col xs={24} sm={8}>
                    <SimpleInput name={['asset', 'brand']} label="brand" />
                  </Col>

                  <Col xs={24} sm={8}>
                    <SimpleInput name={['asset', 'model']} label="model" />
                  </Col>
                </Row>

                <Row gutter={ROW_GUTTER}>
                  <Col xs={24} sm={8}>
                    <SimpleInput name={['asset', 'serialNumber']} label="serial_number" />
                  </Col>

                  <Col xs={24} sm={16}>
                    <SimpleInput name={['asset', 'accessories']} label="accessories" />
                  </Col>
                </Row>

                <Row gutter={ROW_GUTTER}>
                  <Col xs={24} sm={12}>
                    <SimpleInput
                      name={['asset', 'reportedIssue']}
                      label="reported_issue"
                      type="textarea"
                      rows={3}
                    />
                  </Col>

                  <Col xs={24} sm={12}>
                    <SimpleInput
                      name={['asset', 'technicalReport']}
                      label="technical_report"
                      type="textarea"
                      rows={3}
                    />
                  </Col>
                </Row>
              </>
            ),
          },
        ]}
      />

      {/* CARD 4 - OPERATIONAL (order only) */}
      {isOrder && (
        <Card
          title={
            <div className="text-muted">
              <SettingOutlined style={{ marginRight: 8 }} />
              {translateX('operational')}
            </div>
          }
        >
          <Row gutter={ROW_GUTTER}>
            <Col xs={24} sm={12} lg={8}>
              <SimpleInput name="assignedTo" label="assigned_to" />
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item label={translateX('priority')} name="priority">
                <Segmented
                  options={[
                    { label: translateX('low'), value: 'low' },
                    { label: translateX('normal'), value: 'normal' },
                    { label: translateX('high'), value: 'high' },
                    { label: translateX('urgent'), value: 'urgent' },
                  ]}
                  onChange={handlePriorityChange}
                  disabled={isLocked}
                  block
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <SimpleDatePicker
                name="estimatedCompletion"
                label="estimated_completion"
              />
            </Col>
          </Row>

          <Row gutter={ROW_GUTTER}>
            <Col xs={24} sm={12}>
              <SimpleInput
                name="internalNotes"
                label="internal_notes"
                type="textarea"
                rows={3}
              />
            </Col>

            <Col xs={24} sm={12}>
              <SimpleInput
                name="customerNotes"
                label="customer_notes"
                type="textarea"
                rows={3}
              />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ServiceOrdersRecordTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
